import { useQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useMemo } from 'react';

import type { AuditReportData, AuditReport, Community, AuditSession } from '../domain/audit-report-types';
import { GET_AUDIT_REPORT } from '../graphql/queries/getAuditReport';

interface UseGetAuditReportResult {
  reports: AuditReport[];
  community: Community | null;
  session: AuditSession | null;
  loading: boolean;
  errors: DomainError[];
  refetch: () => void;
}

/**
 * Custom hook to fetch audit report data for a specific audit session
 * @param communityId - The community ID
 * @param auditSessionId - The audit session ID
 * @returns Audit reports array, community, session, loading state, and errors
 */
// eslint-disable-next-line complexity
export function useGetAuditReport(communityId: string | undefined, auditSessionId: string | undefined): UseGetAuditReportResult {
  const { data, loading, error, refetch } = useQuery<AuditReportData>(GET_AUDIT_REPORT, {
    variables: {
      auditSessionId,
      communityId,
    },
    skip: !communityId || !auditSessionId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: true, // Show loading during refetch
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  // Transform GraphQL errors to domain errors - only if error exists
  const errors = useMemo(() => (error ? transformErrors(error) : []), [error]);

  // Extract data from nested structure
  const community = data?.community || null;
  const session = data?.community?.auditSession || null;
  const reports = data?.community?.auditSession?.auditReports || [];

  return {
    reports,
    community,
    session,
    loading,
    errors,
    refetch: () => {
      refetch();
    },
  };
}
